import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { PDFRepositoryWidgetInstanceConfig, PDFRepositoryDataContent, RepositoryPDF } from '../../../../../types';
import { useToast } from '../../../../../hooks/useToast';
import { uploadWidgetFile, downloadWidgetFile, deleteWidgetFile } from '../../../../../services/storageService';
import ConfirmationModal from '../../../../common/ConfirmationModal';
import Button from '../../../../ui/Button';
import SearchBar from '../../../../ui/SearchBar';
import PopoutWindow from '../../../../ui/PopoutWindow';
import DropZone from '../../../../ui/DropZone';
import RepositoryModal from '../../../blocks/repository/RepositoryModal';
import { WidgetEmptyState } from '../../../design';
import { useRepositoryItems, useWidgetPrefs } from '../../../blocks/repository';
import { getWidgetIcon } from '../../../registry';

// Import react-pdf styles for text layer
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

// Set up PDF.js worker - use unpkg CDN which is more reliable for exact versions
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// SVG icon constants for Button atoms
const BackIcon = (
  <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const FullscreenIcon = (
  <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
  </svg>
);

const EditIcon = (
  <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const DeleteIcon = (
  <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const CloseIcon = (
  <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

// Highlight interface for storing user highlights
interface PDFHighlight {
  id: string;
  pageNumber: number;
  text: string;
  color: string;
  rects: Array<{ x: number; y: number; width: number; height: number }>;
  createdAt: string;
}

interface PDFRepositoryWidgetRendererProps {
  config: PDFRepositoryWidgetInstanceConfig;
  dashboardData?: Record<string, unknown>;
  onDataChange?: (dataKey: string, newData: PDFRepositoryDataContent) => void;
  readOnly?: boolean;
  assignmentId?: string;
}

// Placeholder shown while PDF thumbnail loads or on error
const PDFPlaceholder: React.FC = () => (
  <div className="w-full h-full bg-neutral-200 animate-pulse flex items-center justify-center text-neutral-400">
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  </div>
);

// Thumbnail component that renders page 1 of a PDF, scaled to fit the container
const PDFThumbnail: React.FC<{ file: string; width?: number }> = ({ file, width = 120 }) => {
  const [ready, setReady] = useState(false);
  if (!file) return <PDFPlaceholder />;
  return (
    <div className="w-full h-full overflow-hidden relative">
      {!ready && <div className="absolute inset-0 z-10"><PDFPlaceholder /></div>}
      <div className={ready ? '' : 'invisible'}>
        <Document
          file={file}
          loading={null}
          error={<PDFPlaceholder />}
        >
          <Page
            pageNumber={1}
            width={width}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            className="[&_canvas]:!w-full [&_canvas]:!h-auto [&_canvas]:object-contain"
            onRenderSuccess={() => setReady(true)}
          />
        </Document>
      </div>
    </div>
  );
};

const formatFileSize = (bytes?: number): string => {
  if (!bytes) return 'Unknown size';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const PDFRepositoryWidgetRenderer: React.FC<PDFRepositoryWidgetRendererProps> = ({
  config,
  dashboardData = {},
  onDataChange,
  readOnly = false,
  assignmentId,
}) => {
  const toast = useToast();

  // Repository CRUD, search, and copy-ref via shared hook
  const {
    items: pdfs, searchQuery, setSearchQuery,
    filteredItems: filteredPDFs, copiedId, handleCopyRef,
    createItem, updateItem, deleteItem,
    deletingItemId: deletingPdfId, setDeletingItemId: setDeletingPdfId,
    confirmDelete,
  } = useRepositoryItems<RepositoryPDF>({
    config,
    dashboardData,
    onDataChange: onDataChange as ((dataKey: string, data: unknown) => void) | undefined,
    itemsKey: 'pdfs',
    idPrefix: 'pdf',
    searchFields: ['title', 'description', 'tags'],
  });

  const [selectedPDFIndex, setSelectedPDFIndex] = useState<number | null>(null);
  const { showList, setShowList, sortBy, setSortBy } = useWidgetPrefs(config.dataKey);
  const [viewMode, setViewMode] = useState<'grid' | 'inline'>('grid');

  // Sort filtered items — pinned always first
  const sortedPDFs = useMemo(() => {
    return [...filteredPDFs].sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      switch (sortBy) {
        case 'title': return a.title.localeCompare(b.title);
        case 'created': return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'updated': return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      }
    });
  }, [filteredPDFs, sortBy]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPDF, setEditingPDF] = useState<RepositoryPDF | null>(null);
  const [newPDFTitle, setNewPDFTitle] = useState('');
  const [newPDFDescription, setNewPDFDescription] = useState('');
  const [uploadData, setUploadData] = useState<string | null>(null);
  const [uploadFileName, setUploadFileName] = useState('');
  const [uploadFileSize, setUploadFileSize] = useState<number>(0);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cache of blob URLs for storage-type PDFs
  const [storageUrls, setStorageUrls] = useState<Record<string, string>>({});
  const useStorageApi = Boolean(assignmentId);

  const loadStoragePdf = useCallback(async (pdf: RepositoryPDF) => {
    if (pdf.pdfType !== 'storage' || !pdf.storageFileId || !assignmentId) return;
    if (storageUrls[pdf.id]) return;
    try {
      const blobUrl = await downloadWidgetFile(assignmentId, config.dataKey, pdf.storageFileId);
      setStorageUrls(prev => ({ ...prev, [pdf.id]: blobUrl }));
    } catch {
      // Failed to load
    }
  }, [assignmentId, config.dataKey, storageUrls]);

  useEffect(() => {
    pdfs.forEach(pdf => {
      if (pdf.pdfType === 'storage' && pdf.storageFileId && !storageUrls[pdf.id]) {
        loadStoragePdf(pdf);
      }
    });
  }, [pdfs, loadStoragePdf, storageUrls]);

  // Helper to get the display data for a PDF
  const getPdfData = (pdf: RepositoryPDF): string => {
    if (pdf.pdfType === 'storage') {
      return storageUrls[pdf.id] || '';
    }
    return pdf.pdfData;
  };

  // PDF viewer state
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);

  // Highlighter state
  const [highlightMode, setHighlightMode] = useState<boolean>(false);
  const [highlightColor, setHighlightColor] = useState<string>('#ffeb3b');
  const [highlights, setHighlights] = useState<Record<string, PDFHighlight[]>>({});
  const [showHighlightPanel, setShowHighlightPanel] = useState<boolean>(false);
  const pageContainerRef = useRef<HTMLDivElement>(null);

  // Available highlight colors
  const highlightColors = [
    { name: 'Yellow', color: '#ffeb3b' },
    { name: 'Green', color: '#a5d6a7' },
    { name: 'Blue', color: '#90caf9' },
    { name: 'Pink', color: '#f48fb1' },
    { name: 'Orange', color: '#ffcc80' },
  ];

  const selectedPDF = selectedPDFIndex !== null && sortedPDFs[selectedPDFIndex]
    ? sortedPDFs[selectedPDFIndex]
    : null;

  const processFile = (file: File) => {
    if (file.type !== 'application/pdf') {
      toast.warning('Please select a PDF file');
      return;
    }

    setUploadFile(file);

    // Read file as base64 for preview (and for non-storage fallback)
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      setUploadData(base64);
      setUploadFileName(file.name);
      setUploadFileSize(file.size);
      setNewPDFTitle(file.name.replace(/\.pdf$/i, '')); // Remove .pdf extension
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!uploadData || !newPDFTitle.trim()) return;

    if (useStorageApi && uploadFile && assignmentId) {
      setIsUploading(true);
      try {
        const fileInfo = await uploadWidgetFile(assignmentId, config.dataKey, uploadFile);
        createItem({
          title: newPDFTitle.trim(),
          description: newPDFDescription.trim() || undefined,
          tags: [],
          pdfData: '',
          pdfType: 'storage',
          storageFileId: fileInfo.id,
          fileName: uploadFileName,
          fileSize: uploadFileSize,
        } as Omit<RepositoryPDF, 'id' | 'createdAt' | 'updatedAt'>);
      } catch {
        toast.error('Failed to upload PDF');
      } finally {
        setIsUploading(false);
      }
    } else {
      createItem({
        title: newPDFTitle.trim(),
        description: newPDFDescription.trim() || undefined,
        tags: [],
        pdfData: uploadData,
        pdfType: 'base64',
        fileName: uploadFileName,
        fileSize: uploadFileSize,
      } as Omit<RepositoryPDF, 'id' | 'createdAt' | 'updatedAt'>);
    }
    resetUploadModal();
  };

  const resetUploadModal = () => {
    setShowUploadModal(false);
    setUploadData(null);
    setUploadFile(null);
    setNewPDFTitle('');
    setNewPDFDescription('');
    setUploadFileName('');
    setUploadFileSize(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDelete = (pdfId: string) => {
    if (!onDataChange) return;
    deleteItem(pdfId);
  };

  const confirmDeletePdf = async () => {
    if (deletingPdfId && assignmentId) {
      const pdf = pdfs.find(p => p.id === deletingPdfId);
      if (pdf?.pdfType === 'storage' && pdf.storageFileId) {
        try {
          await deleteWidgetFile(assignmentId, config.dataKey, pdf.storageFileId);
        } catch {
          // Storage delete failed - still remove from local data
        }
      }
    }
    confirmDelete();
    setSelectedPDFIndex(null);
  };

  const handleEditSave = () => {
    if (!editingPDF) return;
    updateItem(editingPDF.id, {
      title: newPDFTitle.trim() || editingPDF.title,
      description: newPDFDescription.trim() || undefined,
    } as Partial<RepositoryPDF>);
    setShowEditModal(false);
    setEditingPDF(null);
  };

  const openInNewTab = (pdf: RepositoryPDF) => {
    if (pdf.pdfType === 'storage' && pdf.storageFileId && assignmentId) {
      window.open(`/viewer/${assignmentId}/${config.dataKey}/${pdf.storageFileId}`, '_blank');
    } else if (pdf.pdfType === 'base64' && pdf.pdfData) {
      // Base64: convert to blob and open
      const byteString = atob(pdf.pdfData.split(',')[1] || pdf.pdfData);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
      const blob = new Blob([ab], { type: 'application/pdf' });
      window.open(URL.createObjectURL(blob), '_blank');
    } else {
      setIsFullscreen(true);
    }
  };

  const openEditModal = (pdf: RepositoryPDF) => {
    setEditingPDF(pdf);
    setNewPDFTitle(pdf.title);
    setNewPDFDescription(pdf.description || '');
    setShowEditModal(true);
  };

  const navigatePDF = (direction: 'prev' | 'next') => {
    if (selectedPDFIndex === null) return;
    const newIndex = direction === 'prev'
      ? Math.max(0, selectedPDFIndex - 1)
      : Math.min(sortedPDFs.length - 1, selectedPDFIndex + 1);
    setSelectedPDFIndex(newIndex);
    setCurrentPage(1); // Reset to first page when switching PDFs
    setNumPages(0);
  };

  // Handle text selection for highlighting
  const handleTextSelection = useCallback(() => {
    if (!highlightMode || !selectedPDF) return;

    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || !selection.toString().trim()) return;

    const selectedText = selection.toString().trim();
    const range = selection.getRangeAt(0);

    // Get the bounding rects of the selection
    const rects = Array.from(range.getClientRects()).map(rect => {
      const containerRect = pageContainerRef.current?.getBoundingClientRect();
      if (!containerRect) return null;
      return {
        x: rect.x - containerRect.x,
        y: rect.y - containerRect.y,
        width: rect.width,
        height: rect.height,
      };
    }).filter((r): r is { x: number; y: number; width: number; height: number } => r !== null);

    if (rects.length === 0) return;

    const newHighlight: PDFHighlight = {
      id: `hl_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
      pageNumber: currentPage,
      text: selectedText,
      color: highlightColor,
      rects,
      createdAt: new Date().toISOString(),
    };

    setHighlights(prev => ({
      ...prev,
      [selectedPDF.id]: [...(prev[selectedPDF.id] || []), newHighlight],
    }));

    // Clear selection after highlighting
    selection.removeAllRanges();
  }, [highlightMode, selectedPDF, currentPage, highlightColor]);

  // Delete a highlight
  const deleteHighlight = (highlightId: string) => {
    if (!selectedPDF) return;
    setHighlights(prev => ({
      ...prev,
      [selectedPDF.id]: (prev[selectedPDF.id] || []).filter(h => h.id !== highlightId),
    }));
  };

  // Get highlights for current PDF and page
  const currentHighlights = selectedPDF
    ? (highlights[selectedPDF.id] || []).filter(h => h.pageNumber === currentPage)
    : [];

  // Add mouseup listener for highlighting
  useEffect(() => {
    if (highlightMode && pageContainerRef.current) {
      const handleMouseUp = () => handleTextSelection();
      const container = pageContainerRef.current;
      container.addEventListener('mouseup', handleMouseUp);
      return () => container.removeEventListener('mouseup', handleMouseUp);
    }
  }, [highlightMode, handleTextSelection]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setCurrentPage(1);
  };

  const getThumbnailSize = () => {
    switch (config.thumbnailSize) {
      case 'small': return 'w-24 h-32';
      case 'large': return 'w-48 h-64';
      default: return 'w-32 h-44';
    }
  };

  // No data key configured
  if (!config.dataKey) {
    return (
      <WidgetEmptyState
        icon={<span className="text-2xl">{getWidgetIcon('pdf')}</span>}
        title="No data key configured"
        description="Configure a data key in the widget settings."
      />
    );
  }

  return (
    <div className="h-full flex flex-col bg-surface overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b bg-neutral-50 flex items-center gap-3">
        {/* Search */}
        <SearchBar
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search..."
          className="flex-1"
        />

        {/* Count */}
        <p className="text-xs text-neutral-500 flex-shrink-0 ml-auto">
          {searchQuery
            ? `${filteredPDFs.length} of ${pdfs.length} items`
            : `${pdfs.length} items`
          }
        </p>

        {/* Sort */}
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as 'title' | 'created' | 'updated')}
          className="text-xs border border-neutral-200 rounded-lg px-2 h-9 bg-surface text-neutral-700 flex-shrink-0"
        >
          <option value="updated">Last Updated</option>
          <option value="created">Date Added</option>
          <option value="title">Title</option>
        </select>

        {/* View toggle */}
        <div className="flex border border-neutral-200 rounded-lg overflow-hidden flex-shrink-0 h-9">
          <button
            onClick={() => setShowList(true)}
            className={`px-2.5 flex items-center ${showList ? 'bg-neutral-200 text-neutral-700' : 'text-neutral-400 hover:text-neutral-600'}`}
            title="List view"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
          </button>
          <button
            onClick={() => setShowList(false)}
            className={`px-2.5 flex items-center ${!showList ? 'bg-neutral-200 text-neutral-700' : 'text-neutral-400 hover:text-neutral-600'}`}
            title="Grid view"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
          </button>
        </div>

        {/* Upload Button */}
        {!readOnly && config.allowUpload !== false && (
          <Button variant="primary" size="sm" onClick={() => setShowUploadModal(true)} className="min-w-[90px] justify-center">
            Upload
          </Button>
        )}
      </div>

      {/* Content */}
      <div className={`flex-1 overflow-auto ${showList ? '' : 'p-4'}`}>
        {sortedPDFs.length === 0 ? (
          <WidgetEmptyState
            icon={<span className="text-2xl">{getWidgetIcon('pdf')}</span>}
            title={searchQuery ? 'No PDFs match your search' : 'No PDFs yet'}
            description={searchQuery ? 'Try a different search term' : (!readOnly && config.allowUpload !== false ? 'Click Upload to add your first PDF' : 'No PDFs have been added')}
            action={!searchQuery && !readOnly && config.allowUpload !== false ? <Button variant="primary" size="sm" onClick={() => setShowUploadModal(true)}>Upload</Button> : undefined}
          />
        ) : !showList ? (
          /* Grid View */
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {sortedPDFs.map((pdf, index) => (
              <div
                key={pdf.id}
                className={`group relative rounded-lg overflow-hidden border-2 cursor-pointer transition-all ${
                  selectedPDFIndex === index ? 'border-primary-500 ring-2 ring-primary-200' : 'border-neutral-200 hover:border-neutral-300'
                }`}
                onClick={() => openInNewTab(pdf)}
              >
                <div className={`${getThumbnailSize()} w-full bg-neutral-100 overflow-hidden`}>
                  <PDFThumbnail file={getPdfData(pdf)} width={160} />
                </div>
                {/* Pin button — top left */}
                <button
                  onClick={(e) => { e.stopPropagation(); updateItem(pdf.id, { isPinned: !pdf.isPinned } as Partial<RepositoryPDF>); }}
                  className={`absolute top-1 left-1 p-1 rounded transition-all ${
                    pdf.isPinned ? 'text-warning-500 bg-black/70' : 'text-white bg-black/70 opacity-0 group-hover:opacity-100 hover:bg-black/90'
                  }`}
                  title={pdf.isPinned ? 'Unpin' : 'Pin to top'}
                >
                  <svg className="w-3.5 h-3.5" fill={pdf.isPinned ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" /></svg>
                </button>
                {/* Overlay buttons — top right */}
                <div className="absolute top-1 right-1 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-all">
                  <button
                    onClick={(e) => handleCopyRef(pdf.id, e)}
                    className={`p-1 rounded ${copiedId === pdf.id ? 'bg-success-500 text-white' : 'bg-black/70 text-white hover:bg-black/90'}`}
                    title="Copy link"
                  >
                    {copiedId === pdf.id ? (
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    ) : (
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                    )}
                  </button>
                  <a
                    href={getPdfData(pdf) || undefined}
                    download={pdf.title}
                    onClick={(e) => e.stopPropagation()}
                    className="p-1 rounded bg-black/70 text-white hover:bg-black/90"
                    title="Download"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                  </a>
                </div>
                <div className="p-2 bg-surface">
                  <p className="text-sm font-medium text-neutral-900 truncate">{pdf.title}</p>
                  <p className="text-xs text-neutral-500">{formatFileSize(pdf.fileSize)}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* List View */
          <div className="divide-y divide-neutral-100">
            {sortedPDFs.map((pdf, index) => (
              <div
                key={pdf.id}
                className={`group flex items-center gap-3 h-[72px] px-4 cursor-pointer transition-all ${
                  selectedPDFIndex === index ? 'bg-primary-50 border-l-4 border-l-primary-500' : 'hover:bg-neutral-50'
                }`}
                onClick={() => openInNewTab(pdf)}
              >
                <button
                  onClick={(e) => { e.stopPropagation(); updateItem(pdf.id, { isPinned: !pdf.isPinned } as Partial<RepositoryPDF>); }}
                  className={`p-1 rounded flex-shrink-0 transition-all ${
                    pdf.isPinned ? 'text-warning-500' : 'text-neutral-300 opacity-0 group-hover:opacity-100 hover:text-warning-500'
                  }`}
                  title={pdf.isPinned ? 'Unpin' : 'Pin to top'}
                >
                  <svg className="w-4 h-4" fill={pdf.isPinned ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" /></svg>
                </button>
                <div className="w-14 h-14 bg-neutral-100 rounded overflow-hidden flex-shrink-0">
                  <PDFThumbnail file={getPdfData(pdf)} width={40} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-neutral-900 truncate">{pdf.title}</p>
                  <p className="text-sm text-neutral-500 truncate mt-0.5">
                    {formatFileSize(pdf.fileSize)}{pdf.pageCount ? ` • ${pdf.pageCount} pages` : ''}
                    {pdf.description ? ` — ${pdf.description}` : ''}
                  </p>
                </div>
                <button
                  onClick={(e) => handleCopyRef(pdf.id, e)}
                  className={`p-1.5 rounded flex-shrink-0 transition-all ${
                    copiedId === pdf.id ? 'text-success-600' : 'text-neutral-400 opacity-0 group-hover:opacity-100 hover:text-neutral-600'
                  }`}
                  title={copiedId === pdf.id ? 'Copied!' : 'Copy link'}
                >
                  {copiedId === pdf.id
                    ? <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                  }
                </button>
                <a
                  href={getPdfData(pdf) || undefined}
                  download={pdf.title}
                  onClick={(e) => e.stopPropagation()}
                  className="p-1.5 rounded flex-shrink-0 text-neutral-400 opacity-0 group-hover:opacity-100 hover:text-neutral-600 transition-all"
                  title="Download"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                </a>
                <p className="text-xs text-neutral-400 flex-shrink-0">
                  {new Date(pdf.createdAt).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Inline PDF Viewer - Shows inside the widget panel */}
      {selectedPDF && viewMode === 'inline' && !isFullscreen && (
        <div className="absolute inset-0 z-20 bg-surface flex flex-col">
          {/* Compact Viewer Header */}
          <div className="px-3 py-2 border-b bg-neutral-50 flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              {/* Back to grid button */}
              <Button
                variant="ghost"
                size="xs"
                icon={BackIcon}
                onClick={() => {
                  setViewMode('grid');
                  setSelectedPDFIndex(null);
                }}
                title="Back to grid"
              />
              <div className="min-w-0 flex-1">
                <h3 className="font-medium text-neutral-900 text-sm truncate">{selectedPDF.title}</h3>
                <div className="flex flex-wrap gap-1 items-center">
                  {/* Reference Link */}
                  <button
                    onClick={() => handleCopyRef(selectedPDF.id)}
                    className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono transition-colors ${
                      copiedId === selectedPDF.id
                        ? 'bg-success-100 text-success-700'
                        : 'bg-neutral-100 text-neutral-600 hover:bg-primary-100 hover:text-primary-700'
                    }`}
                    title="Click to copy reference link"
                  >
                    <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                    {copiedId === selectedPDF.id ? 'Copied!' : selectedPDF.id}
                  </button>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {/* Fullscreen button */}
              <Button
                variant="ghost"
                size="xs"
                icon={FullscreenIcon}
                onClick={() => selectedPDF && openInNewTab(selectedPDF)}
                title="Open in new tab"
              />
              {!readOnly && config.allowTagging !== false && (
                <Button
                  variant="ghost"
                  size="xs"
                  icon={EditIcon}
                  onClick={() => openEditModal(selectedPDF)}
                  title="Edit"
                />
              )}
              {!readOnly && config.allowDelete !== false && (
                <Button
                  variant="ghost"
                  size="xs"
                  icon={DeleteIcon}
                  onClick={() => handleDelete(selectedPDF.id)}
                  title="Delete"
                />
              )}
              <Button
                variant="ghost"
                size="xs"
                icon={CloseIcon}
                onClick={() => {
                  setViewMode('grid');
                  setSelectedPDFIndex(null);
                }}
                title="Close"
              />
            </div>
          </div>

          {/* Compact PDF Toolbar */}
          <div className="px-2 py-1.5 border-b bg-neutral-50 flex items-center justify-between gap-2 flex-wrap">
            {/* Page Navigation */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                className="p-1 rounded hover:bg-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="text-xs text-neutral-600">
                {currentPage}/{numPages || '?'}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))}
                disabled={currentPage >= numPages}
                className="p-1 rounded hover:bg-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Highlighter Controls */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => setHighlightMode(!highlightMode)}
                className={`p-1 rounded ${
                  highlightMode
                    ? 'bg-warning-100 text-warning-700'
                    : 'hover:bg-neutral-200 text-neutral-600'
                }`}
                title={highlightMode ? 'Disable Highlighter' : 'Enable Highlighter'}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
              {highlightMode && (
                <>
                  {highlightColors.map(({ name, color }) => (
                    <button
                      key={color}
                      onClick={() => setHighlightColor(color)}
                      className={`w-4 h-4 rounded-full border ${
                        highlightColor === color ? 'border-neutral-700 scale-110' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color }}
                      title={name}
                    />
                  ))}
                </>
              )}
              <button
                onClick={() => setShowHighlightPanel(!showHighlightPanel)}
                className={`p-1 rounded ${
                  showHighlightPanel ? 'bg-primary-100 text-primary-700' : 'hover:bg-neutral-200 text-neutral-600'
                }`}
                title="View Highlights"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
              </button>
            </div>

            {/* Zoom Controls */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => setScale(s => Math.max(0.5, s - 0.25))}
                className="p-1 rounded hover:bg-neutral-200"
                title="Zoom Out"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
              </button>
              <span className="text-xs text-neutral-600 w-10 text-center">
                {Math.round(scale * 100)}%
              </span>
              <button
                onClick={() => setScale(s => Math.min(3, s + 0.25))}
                className="p-1 rounded hover:bg-neutral-200"
                title="Zoom In"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>
          </div>

          {/* PDF Content with Highlights Panel */}
          <div className="flex-1 flex overflow-hidden">
            {/* Main PDF Viewer */}
            <div className="flex-1 overflow-auto bg-neutral-100 flex items-start justify-center p-2 relative">
              <div
                ref={pageContainerRef}
                className={`relative ${highlightMode ? 'cursor-text select-text' : ''}`}
                style={{ userSelect: highlightMode ? 'text' : 'none' }}
              >
                <Document
                  file={getPdfData(selectedPDF)}
                  onLoadSuccess={onDocumentLoadSuccess}
                  loading={
                    <div className="flex items-center justify-center h-48">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto mb-2"></div>
                        <p className="text-neutral-500 text-sm">Loading...</p>
                      </div>
                    </div>
                  }
                  error={
                    <div className="text-center text-danger-500 p-4">
                      <span className="text-2xl block mb-1"><FontAwesomeIcon icon={faTriangleExclamation} /></span>
                      <p className="text-sm">Failed to load PDF</p>
                    </div>
                  }
                >
                  <Page
                    pageNumber={currentPage}
                    scale={scale}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                  />
                </Document>

                {/* Render highlight overlays */}
                {currentHighlights.map(highlight => (
                  <div key={highlight.id} className="pointer-events-none">
                    {highlight.rects.map((rect, i) => (
                      <div
                        key={i}
                        className="absolute mix-blend-multiply"
                        style={{
                          left: rect.x,
                          top: rect.y,
                          width: rect.width,
                          height: rect.height,
                          backgroundColor: highlight.color,
                          opacity: 0.4,
                        }}
                      />
                    ))}
                  </div>
                ))}
              </div>

              {/* Highlight mode indicator */}
              {highlightMode && (
                <div className="absolute top-1 left-1/2 -translate-x-1/2 px-2 py-1 bg-warning-100 text-warning-800 text-xs rounded-full shadow-sm flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                  Select text
                </div>
              )}

              {/* PDF Navigation Arrows */}
              {selectedPDFIndex !== null && selectedPDFIndex > 0 && (
                <button
                  onClick={(e) => { e.stopPropagation(); navigatePDF('prev'); }}
                  aria-label="Previous PDF"
                  className="absolute left-1 top-1/2 -translate-y-1/2 p-1 bg-surface/90 rounded-full shadow hover:bg-surface"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              {selectedPDFIndex !== null && selectedPDFIndex < sortedPDFs.length - 1 && (
                <button
                  onClick={(e) => { e.stopPropagation(); navigatePDF('next'); }}
                  aria-label="Next PDF"
                  className="absolute right-1 top-1/2 -translate-y-1/2 p-1 bg-surface/90 rounded-full shadow hover:bg-surface"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
            </div>

            {/* Highlights Side Panel (compact) */}
            {showHighlightPanel && (
              <div className="w-48 border-l bg-surface flex flex-col">
                <div className="px-2 py-1.5 border-b bg-neutral-50">
                  <h4 className="font-medium text-neutral-900 text-xs">Highlights</h4>
                  <p className="text-[10px] text-neutral-500">
                    {(highlights[selectedPDF.id] || []).length} total
                  </p>
                </div>
                <div className="flex-1 overflow-auto p-1">
                  {(highlights[selectedPDF.id] || []).length === 0 ? (
                    <div className="text-center text-neutral-500 py-4">
                      <p className="text-xs">No highlights yet</p>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {(highlights[selectedPDF.id] || []).map(highlight => (
                        <div
                          key={highlight.id}
                          role="button"
                          tabIndex={0}
                          className={`p-1.5 rounded border cursor-pointer hover:bg-neutral-50 text-xs ${
                            highlight.pageNumber === currentPage ? 'ring-1 ring-primary-400' : ''
                          }`}
                          onClick={() => setCurrentPage(highlight.pageNumber)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setCurrentPage(highlight.pageNumber);
                            }
                          }}
                        >
                          <div className="flex items-start gap-1">
                            <div
                              className="w-2 h-2 rounded-full flex-shrink-0 mt-0.5"
                              style={{ backgroundColor: highlight.color }}
                            />
                            <p className="flex-1 text-neutral-700 line-clamp-2">
                              {highlight.text}
                            </p>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteHighlight(highlight.id);
                              }}
                              aria-label="Delete highlight"
                              className="text-neutral-400 hover:text-danger-500"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                          <p className="text-[10px] text-neutral-400 ml-3">p.{highlight.pageNumber}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-2 py-1 border-t bg-neutral-50 flex items-center justify-between text-xs text-neutral-500">
            <span>{(selectedPDFIndex ?? 0) + 1} of {sortedPDFs.length}</span>
            {selectedPDF.description && (
              <span className="truncate ml-2">{selectedPDF.description}</span>
            )}
          </div>
        </div>
      )}

      {/* Popout PDF Viewer */}
      <PopoutWindow
        isOpen={selectedPDF !== null && isFullscreen}
        onClose={() => {
          setIsFullscreen(false);
          setViewMode('grid');
          setSelectedPDFIndex(null);
        }}
        title={selectedPDF?.title || 'PDF Viewer'}
        initialScale={0.7}
        headerActions={
          selectedPDF ? (
            <>
              <button
                onClick={() => handleCopyRef(selectedPDF.id)}
                className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono transition-colors ${
                  copiedId === selectedPDF.id
                    ? 'bg-success-100 text-success-700'
                    : 'bg-neutral-100 text-neutral-500 hover:bg-primary-100 hover:text-primary-700'
                }`}
                title="Copy reference link"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                {copiedId === selectedPDF.id ? 'Copied!' : 'ref'}
              </button>
              {!readOnly && config.allowTagging !== false && (
                <button onClick={() => openEditModal(selectedPDF)} className="p-1 rounded hover:bg-neutral-200 text-neutral-500 hover:text-neutral-700 [&_svg]:w-3.5 [&_svg]:h-3.5" title="Edit title">
                  {EditIcon}
                </button>
              )}
              {!readOnly && config.allowDelete !== false && (
                <button onClick={() => handleDelete(selectedPDF.id)} className="p-1 rounded hover:bg-danger-100 text-neutral-500 hover:text-danger-600 [&_svg]:w-3.5 [&_svg]:h-3.5" title="Delete">
                  {DeleteIcon}
                </button>
              )}
            </>
          ) : undefined
        }
      >
        {selectedPDF && (
          <div className="h-full flex flex-col">
            {/* PDF Toolbar */}
            <div className="px-4 py-2 border-b bg-neutral-50 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                  className="p-1.5 rounded hover:bg-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <span className="text-sm text-neutral-600">
                  Page {currentPage} of {numPages || '?'}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))}
                  disabled={currentPage >= numPages}
                  className="p-1.5 rounded hover:bg-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              {/* Highlighter Controls */}
              <div className="flex items-center gap-2">
                <div className="border-r pr-2 mr-2 flex items-center gap-1">
                  <button
                    onClick={() => setHighlightMode(!highlightMode)}
                    className={`p-1.5 rounded flex items-center gap-1 ${
                      highlightMode
                        ? 'bg-warning-100 text-warning-700 ring-2 ring-warning-400'
                        : 'hover:bg-neutral-200 text-neutral-600'
                    }`}
                    title={highlightMode ? 'Disable Highlighter' : 'Enable Highlighter'}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    <span className="text-xs font-medium">Highlight</span>
                  </button>
                  {highlightMode && (
                    <div className="flex items-center gap-1 ml-1">
                      {highlightColors.map(({ name, color }) => (
                        <button
                          key={color}
                          onClick={() => setHighlightColor(color)}
                          className={`w-5 h-5 rounded-full border-2 ${
                            highlightColor === color ? 'border-neutral-700 scale-110' : 'border-transparent'
                          }`}
                          style={{ backgroundColor: color }}
                          title={name}
                        />
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setShowHighlightPanel(!showHighlightPanel)}
                  className={`p-1.5 rounded ${
                    showHighlightPanel ? 'bg-primary-100 text-primary-700' : 'hover:bg-neutral-200 text-neutral-600'
                  }`}
                  title="View Highlights"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                </button>
              </div>

              {/* Zoom Controls */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setScale(s => Math.max(0.5, s - 0.25))}
                  className="p-1.5 rounded hover:bg-neutral-200"
                  title="Zoom Out"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                  </svg>
                </button>
                <span className="text-sm text-neutral-600 min-w-[4rem] text-center">
                  {Math.round(scale * 100)}%
                </span>
                <button
                  onClick={() => setScale(s => Math.min(3, s + 0.25))}
                  className="p-1.5 rounded hover:bg-neutral-200"
                  title="Zoom In"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>
            </div>

            {/* PDF Content with Highlights Panel */}
            <div className="flex-1 flex overflow-hidden">
              {/* Main PDF Viewer */}
              <div className="flex-1 overflow-auto bg-neutral-200 flex items-start justify-center p-4 relative">
                <div
                  ref={pageContainerRef}
                  className={`relative ${highlightMode ? 'cursor-text select-text' : ''}`}
                  style={{ userSelect: highlightMode ? 'text' : 'none' }}
                >
                  <Document
                    file={getPdfData(selectedPDF)}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={
                      <div className="flex items-center justify-center h-64">
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
                          <p className="text-neutral-500">Loading PDF...</p>
                        </div>
                      </div>
                    }
                    error={
                      <div className="text-center text-danger-500 p-8">
                        <span className="text-4xl block mb-2"><FontAwesomeIcon icon={faTriangleExclamation} /></span>
                        <p>Failed to load PDF</p>
                      </div>
                    }
                  >
                    <Page
                      pageNumber={currentPage}
                      scale={scale}
                      renderTextLayer={true}
                      renderAnnotationLayer={true}
                    />
                  </Document>

                  {/* Render highlight overlays */}
                  {currentHighlights.map(highlight => (
                    <div key={highlight.id} className="pointer-events-none">
                      {highlight.rects.map((rect, i) => (
                        <div
                          key={i}
                          className="absolute mix-blend-multiply"
                          style={{
                            left: rect.x,
                            top: rect.y,
                            width: rect.width,
                            height: rect.height,
                            backgroundColor: highlight.color,
                            opacity: 0.4,
                          }}
                        />
                      ))}
                    </div>
                  ))}
                </div>

                {/* Highlight mode indicator */}
                {highlightMode && (
                  <div className="absolute top-2 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-warning-100 text-warning-800 text-sm rounded-full shadow-md flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    Select text to highlight
                  </div>
                )}

                {/* PDF Navigation Arrows */}
                {selectedPDFIndex !== null && selectedPDFIndex > 0 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); navigatePDF('prev'); }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-surface rounded-full shadow-lg hover:bg-neutral-100"
                    title="Previous PDF"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                {selectedPDFIndex !== null && selectedPDFIndex < sortedPDFs.length - 1 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); navigatePDF('next'); }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-surface rounded-full shadow-lg hover:bg-neutral-100"
                    title="Next PDF"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Highlights Side Panel */}
              {showHighlightPanel && (
                <div className="w-72 border-l bg-surface flex flex-col">
                  <div className="px-4 py-3 border-b bg-neutral-50">
                    <h4 className="font-medium text-neutral-900">Highlights</h4>
                    <p className="text-xs text-neutral-500">
                      {(highlights[selectedPDF.id] || []).length} highlight{(highlights[selectedPDF.id] || []).length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <div className="flex-1 overflow-auto p-2">
                    {(highlights[selectedPDF.id] || []).length === 0 ? (
                      <div className="text-center text-neutral-500 py-8">
                        <svg className="w-12 h-12 mx-auto mb-2 text-neutral-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                        <p className="text-sm">No highlights yet</p>
                        <p className="text-xs mt-1">Enable highlight mode and select text</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {(highlights[selectedPDF.id] || []).map(highlight => (
                          <div
                            key={highlight.id}
                            className={`p-2 rounded border cursor-pointer hover:bg-neutral-50 ${
                              highlight.pageNumber === currentPage ? 'ring-2 ring-primary-400' : ''
                            }`}
                            onClick={() => setCurrentPage(highlight.pageNumber)}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div
                                className="w-3 h-3 rounded-full flex-shrink-0 mt-1"
                                style={{ backgroundColor: highlight.color }}
                              />
                              <p className="flex-1 text-sm text-neutral-700 line-clamp-3">
                                &ldquo;{highlight.text}&rdquo;
                              </p>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteHighlight(highlight.id);
                                }}
                                className="p-1 text-neutral-400 hover:text-danger-500 rounded"
                                title="Delete highlight"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                            <p className="text-xs text-neutral-400 mt-1 ml-5">
                              Page {highlight.pageNumber}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Notes + Navigation footer */}
            <div className="border-t bg-neutral-50 flex-shrink-0">
              {!readOnly && (
                <div className="px-4 py-2">
                  <textarea
                    value={selectedPDF.description || ''}
                    onChange={(e) => updateItem(selectedPDF.id, { description: e.target.value || undefined } as Partial<RepositoryPDF>)}
                    placeholder="Add notes about this PDF..."
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg resize-none bg-surface placeholder-neutral-400"
                  />
                </div>
              )}
              {readOnly && selectedPDF.description && (
                <div className="px-4 py-2">
                  <p className="text-sm text-neutral-600">{selectedPDF.description}</p>
                </div>
              )}
              <div className="px-4 py-1.5 border-t flex items-center justify-between text-xs text-neutral-500">
                <span>PDF {(selectedPDFIndex ?? 0) + 1} of {sortedPDFs.length}</span>
                <span>{formatFileSize(selectedPDF.fileSize)}{numPages > 0 && ` • ${numPages} pages`}</span>
              </div>
            </div>
          </div>
        )}
      </PopoutWindow>

      {/* Upload Modal */}
      <RepositoryModal
        isOpen={showUploadModal}
        onClose={resetUploadModal}
        title="Upload PDF"
        onSave={handleUpload}
        saveLabel={isUploading ? 'Uploading...' : 'Upload'}
        saveDisabled={!uploadData || !newPDFTitle.trim() || isUploading}
        hideFields={!uploadData}
        itemTitle={newPDFTitle}
        onTitleChange={setNewPDFTitle}
        description={newPDFDescription}
        onDescriptionChange={setNewPDFDescription}
        topChildren={
          uploadData ? (
            <div className="border rounded-lg p-3 bg-neutral-50 flex items-center gap-3 relative">
              <span className="text-3xl">{getWidgetIcon('pdf')}</span>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-neutral-900 truncate">{uploadFileName}</p>
                <p className="text-sm text-neutral-500">{formatFileSize(uploadFileSize)}</p>
              </div>
              <button
                onClick={() => { setUploadData(null); setUploadFile(null); setNewPDFTitle(''); setUploadFileName(''); setUploadFileSize(0); }}
                className="p-1 text-neutral-400 hover:text-neutral-600 rounded hover:bg-neutral-200"
                title="Remove"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ) : (
            <DropZone accept="application/pdf" onFile={processFile} label="Drop PDF here or click to browse" hint="PDF files only" />
          )
        }
      />

      {/* Edit Modal */}
      <RepositoryModal
        isOpen={showEditModal && editingPDF !== null}
        onClose={() => setShowEditModal(false)}
        title="Edit PDF"
        onSave={handleEditSave}
        saveLabel="Save Changes"
        itemTitle={newPDFTitle}
        onTitleChange={setNewPDFTitle}
        description={newPDFDescription}
        onDescriptionChange={setNewPDFDescription}
        topChildren={
          editingPDF ? (
            <div className="border rounded-lg p-3 bg-neutral-50 flex items-center gap-3">
              <span className="text-3xl">{getWidgetIcon('pdf')}</span>
              <div>
                <p className="font-medium text-neutral-900">{editingPDF.fileName || 'PDF File'}</p>
                <p className="text-sm text-neutral-500">{formatFileSize(editingPDF.fileSize)}</p>
              </div>
            </div>
          ) : undefined
        }
      />

      <ConfirmationModal
        isOpen={deletingPdfId !== null}
        onClose={() => setDeletingPdfId(null)}
        onAction={confirmDeletePdf}
        title="Delete PDF"
        description="Are you sure you want to delete this PDF?"
        actionText="Delete"
      />
    </div>
  );
};

export default PDFRepositoryWidgetRenderer;
